/**
 * BroadcastHud — the DOM overlay graphics for /watch (the "TV lower-thirds").
 *
 * Read-only: a pot bug, a king + countdown chip, and a winner lower-third. It
 * composes the same `KothLobbyController` state the player HUD reads, plus the
 * director's current shot from the store, so the winner card raises in sync with
 * the WINNER camera cut. Everything is pointer-events:none so it never eats
 * input and never appears in a capture as interactive chrome.
 */
import { useRef, type CSSProperties } from "react";

import { useStore } from "../store";
import { useChainNow } from "../util/useChainNow";
import type { KothLobbyController } from "../games/last-touch/useKothLobbyController";

function lamportsToSol(bnLike: { toString(): string } | null | undefined): string {
  if (!bnLike) return "0.000";
  const n = Number(bnLike.toString()) / 1e9;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function shortKey(k: string): string {
  return k.length <= 8 ? k : `${k.slice(0, 4)}…${k.slice(-4)}`;
}

export function BroadcastHud({
  controller,
}: {
  controller: KothLobbyController;
}): JSX.Element {
  const round = controller.round;
  const shot = useStore((s) => s.broadcastShot);
  const chainNow = useChainNow(1000);
  // `koth_end_round` zeroes `pot_lamports` on settlement, so reading it live
  // would make the winner card (the money shot) read 0.000 SOL. Latch the last
  // non-zero pot seen while the round was Open and show that once settled.
  const lastPotRef = useRef<{ toString(): string } | null>(null);

  if (!round) {
    return (
      <div style={cornerBug}>
        <div style={bugLabel}>KING OF THE HILL</div>
        <div style={bugSub}>Waiting for the next round…</div>
      </div>
    );
  }

  const settled = round.status === 1;
  if (!settled && round.potLamports.toString() !== "0") {
    lastPotRef.current = round.potLamports;
  }
  // Settled → show the latched final pot (falls back to the on-chain 0 only if
  // we never saw the round live, e.g. a fresh load straight onto a settled round).
  const displayPot =
    settled && lastPotRef.current ? lastPotRef.current : round.potLamports;
  const potSol = lamportsToSol(displayPot);
  const expiresAt = Number(round.expiresAt.toString());
  const secsLeft = settled ? 0 : Math.max(0, expiresAt - chainNow);
  const mm = Math.floor(secsLeft / 60)
    .toString()
    .padStart(2, "0");
  const ss = (secsLeft % 60).toString().padStart(2, "0");
  const kingLabel =
    round.touchCount > 0 ? shortKey(round.currentKing.toBase58()) : "—";
  const timerColor =
    secsLeft > 60 ? "#7af09e" : secsLeft > 10 ? "#f4d36a" : "#f47a7a";

  return (
    <>
      <div style={cornerBug}>
        <div style={bugLabel}>POT</div>
        <div style={bugPot}>{potSol} SOL</div>
        <div style={bugSub}>{round.touchCount} touches</div>
      </div>

      <div style={topCenter}>
        <div style={{ ...chip, color: "#ffd9a0" }}>👑 {kingLabel}</div>
        {!settled && (
          <div
            style={{
              ...chip,
              color: timerColor,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            ⏱ {mm}:{ss}
          </div>
        )}
      </div>

      {(settled || shot === "winner") && (
        <div style={lowerThird}>
          <div style={ltLabel}>ROUND WINNER</div>
          <div style={ltKing}>👑 {shortKey(round.currentKing.toBase58())}</div>
          <div style={ltPot}>{potSol} SOL</div>
        </div>
      )}
    </>
  );
}

const overlayBase: CSSProperties = {
  position: "fixed",
  pointerEvents: "none",
  zIndex: 20,
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
  color: "#e8e8ef",
  textShadow: "0 2px 8px rgba(0,0,0,0.7)",
  userSelect: "none",
};

const cornerBug: CSSProperties = {
  ...overlayBase,
  top: 18,
  left: 18,
  padding: "10px 16px",
  background: "rgba(15,26,42,0.72)",
  border: "1px solid rgba(127,196,255,0.25)",
  borderRadius: 10,
  backdropFilter: "blur(6px)",
};

const bugLabel: CSSProperties = {
  fontSize: 11,
  letterSpacing: 2,
  color: "#7fc4ff",
};
const bugPot: CSSProperties = { fontSize: 30, fontWeight: 700, lineHeight: 1.1 };
const bugSub: CSSProperties = { fontSize: 12, color: "#9aa0b4", marginTop: 2 };

const topCenter: CSSProperties = {
  ...overlayBase,
  top: 18,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 10,
};

const chip: CSSProperties = {
  padding: "8px 14px",
  fontSize: 18,
  fontWeight: 600,
  background: "rgba(15,26,42,0.72)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 999,
  backdropFilter: "blur(6px)",
};

const lowerThird: CSSProperties = {
  ...overlayBase,
  bottom: 64,
  left: "50%",
  transform: "translateX(-50%)",
  textAlign: "center",
  padding: "16px 40px",
  background: "rgba(15,26,42,0.85)",
  border: "1px solid rgba(255,217,160,0.4)",
  borderRadius: 14,
};

const ltLabel: CSSProperties = {
  fontSize: 13,
  letterSpacing: 4,
  color: "#ffd9a0",
};
const ltKing: CSSProperties = { fontSize: 34, fontWeight: 800, marginTop: 4 };
const ltPot: CSSProperties = { fontSize: 20, color: "#7af09e", marginTop: 2 };
